import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Share2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { generateQuotePdf, generatePdfFilename } from '../utils/generateQuotePdf';
import { buildCommonPdfLabels } from '../utils/pdfHelpers';
import type { StructuredData, QuoteStatus } from '../api/types';
import { loadLogo, loadContractorSettings } from '../lib/contractorSettings';
import type { ContractorSettings } from '../lib/contractorSettings';
import { useShareQuote } from '../api/hooks';

interface ShareQuoteButtonProps {
  data: {
    structured_data: StructuredData;
    created_at: string;
    contractor: ContractorSettings;
    vatRate: number | null;
    markupPercent: number | null;
    quoteId?: string;
    paymentTermDays?: number;
    locale?: string;
    validityDays?: number;
  };
  quoteId?: string;
  getAccessToken?: () => Promise<string>;
  quoteStatus?: QuoteStatus;
  onStatusChange?: (status: QuoteStatus) => Promise<void>;
  className?: string;
}

export function buildShareText(data: ShareQuoteButtonProps['data'], t: TFunction): string {
  const { structured_data, created_at, contractor, vatRate, markupPercent } = data;
  const date = new Date(created_at).toLocaleDateString();
  const client = structured_data.client_name || t('share.untitled');
  const subtotal = structured_data.grand_total;
  const markupMultiplier = markupPercent ? 1 + markupPercent / 100 : 1;
  const effectiveSubtotal = subtotal * markupMultiplier;

  const lines = [
    ...(contractor.companyName ? [`${t('share.from')}: ${contractor.companyName}`] : []),
    `${t('share.quoteFor')} ${client}`,
    `${t('share.date')}: ${date}`,
    '',
    ...structured_data.items.map((item) => {
      const effectivePrice = item.unit_price * markupMultiplier;
      const lineTotal = item.quantity * effectivePrice;
      return `${item.description}: ${item.quantity} × €${effectivePrice.toFixed(2)} = €${lineTotal.toFixed(2)}`;
    }),
    '',
  ];

  if (vatRate === null) {
    lines.push(`${t('share.grandTotal')}: €${effectiveSubtotal.toFixed(2)}`);
  } else {
    lines.push(`${t('share.subtotal')}: €${effectiveSubtotal.toFixed(2)}`);
    const vatAmount = effectiveSubtotal * (vatRate / 100);
    lines.push(
      `${t('share.vatLine', { rate: vatRate })}: €${vatAmount.toFixed(2)}`,
      `${t('share.total')}: €${(effectiveSubtotal + vatAmount).toFixed(2)}`,
    );
  }

  return lines.join('\n');
}

export function ShareQuoteButton({
  data,
  quoteId,
  getAccessToken,
  quoteStatus,
  onStatusChange,
  className,
}: Readonly<ShareQuoteButtonProps>) {
  const { t, i18n } = useTranslation();
  const [isSharing, setIsSharing] = useState(false);

  const { mutate: shareQuoteMutate } = useShareQuote(getAccessToken ?? (() => Promise.resolve('')));

  const handleShare = useCallback(async () => {
    setIsSharing(true);
    try {
      const locale = i18n.language === 'et' ? 'et-EE' : 'en-US';
      const vatRate = data.vatRate;

      const logo = loadLogo();

      const blob = generateQuotePdf({
        ...data,
        locale: data.locale || locale,
        logo,
        labels: {
          notesHeading: t('pdf.notes'),
          validUntilLabel: t('pdf.validUntil'),
          quoteFor: t('pdf.quoteFor'),
          dateLabel: t('pdf.date'),
          refLabel: t('pdf.ref'),
          paymentTerms: data.paymentTermDays
            ? t('pdf.paymentTerms', { days: data.paymentTermDays })
            : '',
          ...buildCommonPdfLabels(t, vatRate),
        },
      });
      const filename = generatePdfFilename(data.structured_data.client_name, data.created_at);
      const file = new File([blob], filename, { type: 'application/pdf' });

      if (quoteId && getAccessToken) {
        // New share-link flow: generate a share token, copy link, optionally use Web Share
        const contractor = loadContractorSettings();
        shareQuoteMutate(
          { quoteId, contractor: contractor as unknown as Record<string, unknown> },
          {
            onSuccess: (result) => {
              void (async () => {
                const shareUrl = `${globalThis.location.origin}/#quote/${result.share_token}`;

                if (
                  typeof navigator.share === 'function' &&
                  navigator.canShare?.({ files: [file] })
                ) {
                  try {
                    await navigator.share({
                      title: t('share.title'),
                      text: buildShareText(data, t),
                      files: [file],
                    });
                  } catch (err) {
                    // User cancelled native share — fall through to clipboard
                    if (err instanceof DOMException && err.name === 'AbortError') {
                      // just clipboard
                    }
                  }
                }

                await navigator.clipboard.writeText(shareUrl);
                toast.success(t('sharedQuote.linkCopied'));

                // Auto-transition draft → sent
                if (quoteStatus === 'draft' && onStatusChange) {
                  try {
                    await onStatusChange('sent');
                    toast.success(t('quoteStatus.markedAsSent'));
                  } catch {
                    // Share succeeded, status transition failed — non-fatal
                  }
                }

                setIsSharing(false);
              })();
            },
            onError: () => {
              toast.error(t('share.failed'));
              setIsSharing(false);
            },
          },
        );
        // Return early — setIsSharing(false) handled in callbacks
        return;
      }

      // Legacy flow (no quoteId/getAccessToken): PDF share or clipboard text
      if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: t('share.title'),
          text: buildShareText(data, t),
          files: [file],
        });
        toast.success(t('share.success'));
      } else {
        // Fallback: copy text summary to clipboard
        const text = buildShareText(data, t);
        await navigator.clipboard.writeText(text);
        toast.success(t('share.copiedToClipboard'));
      }

      // Auto-transition draft → sent
      if (quoteStatus === 'draft' && onStatusChange) {
        try {
          await onStatusChange('sent');
          toast.success(t('quoteStatus.markedAsSent'));
        } catch {
          // Share succeeded, status transition failed — non-fatal
        }
      }
    } catch (error) {
      // User cancelled share dialog — not an error
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      toast.error(t('share.failed'));
    } finally {
      setIsSharing(false);
    }
  }, [
    data,
    quoteId,
    getAccessToken,
    shareQuoteMutate,
    t,
    i18n.language,
    quoteStatus,
    onStatusChange,
  ]);

  return (
    <Button
      variant="outline"
      aria-label={t('share.button')}
      onClick={handleShare}
      disabled={isSharing}
      className={cn('touch-target', className)}
    >
      {isSharing ? (
        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
      ) : (
        <Share2 className="mr-1 h-4 w-4" />
      )}
      {t('share.button')}
    </Button>
  );
}
